import React, { useState, useEffect } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { IEditionPurchaseLinkConfig, IPlatformPurchaseLinkConfig } from "models/IAppConfig";
import Collapse from "@material-ui/core/Collapse";
import CountryCode from "models/CountryCode";
import useCountrySelect, { ICountries } from "hooks/useCountrySelect";
import usePlatformSelectionLinks from "./platformSelection/usePlatformSelectionLinks";
import Spacer from "components/Spacer";
import defaultCountries from "hooks/useCountrySelect/defaultCountries";
import { useLocation } from "react-router-dom";
import queryString from "query-string";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {},
        headerContainer: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: 35,
        },
    })
);

interface IProps {
    editionPurchaseLinkConfig: IEditionPurchaseLinkConfig;
}

function getDefaultCountry(country: any, possibleCountries: ICountries): CountryCode {
    const defaultCountry = CountryCode.GB;

    if (typeof country !== "string") return defaultCountry;
    if (country.length !== 2) return defaultCountry;

    const countryCode = country.toUpperCase() as CountryCode;
    const parsedCountry = possibleCountries[countryCode];

    if (typeof parsedCountry === "undefined") return defaultCountry;

    return countryCode;
}

export default function BuyNowDialogController(props: IProps) {
    const classes = useStyles();
    const { editionPurchaseLinkConfig } = props;
    let location = useLocation();

    const queryStringCountry = queryString.parse(location.search).country;

    const possibleCountries = defaultCountries;
    const defaultCountry = getDefaultCountry(queryStringCountry, possibleCountries);

    const defaultShowOptions = false;
    const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode>(defaultCountry);
    const [showOptions, setShowOptions] = useState<boolean>(false);

    const { countrySelectLabel, countrySelectOptions } = useCountrySelect({
        onSelectedCountryChange: (_selectedCountry: CountryCode) =>
            setSelectedCountryCode(_selectedCountry),
        onShowOptionsChange: (_showOptions: boolean) => setShowOptions(_showOptions),
        countries: possibleCountries,
        defaultCountry,
        defaultShowOptions,
    });

    const [currentSelectedPlatform, setCurrentSelectedPlatform] = useState<
        IPlatformPurchaseLinkConfig | undefined
    >();

    useEffect(() => {
        setCurrentSelectedPlatform(undefined);
    }, [selectedCountryCode]);

    const selectPlatform = (platform: IPlatformPurchaseLinkConfig) => {
        if (platform.platformCode === currentSelectedPlatform?.platformCode) {
            setCurrentSelectedPlatform(undefined);
        } else {
            setCurrentSelectedPlatform(platform);
        }
    };

    const { platformLinks, storeLinks, backButton } = usePlatformSelectionLinks({
        selectedCountry: selectedCountryCode,
        editionPurchaseLinkConfig: editionPurchaseLinkConfig,
        currentSelectedPlatform: currentSelectedPlatform,
        onPlatformSelect: selectPlatform,
        onBack: () => {
            setCurrentSelectedPlatform(undefined);
        },
    });

    const showStoreLinks = storeLinks !== null;

    return (
        <div className={classes.root}>
            <div className={classes.headerContainer}>
                <div>{showOptions ? null : backButton}</div>
                <div>{countrySelectLabel}</div>
            </div>

            <Spacer size={2} />

            <Collapse in={showOptions}>{countrySelectOptions}</Collapse>

            <Collapse in={!showOptions}>
                {typeof editionPurchaseLinkConfig === "undefined" ? (
                    <div></div>
                ) : (
                    <>
                        <Collapse in={!showStoreLinks}>{platformLinks}</Collapse>
                        <Collapse in={showStoreLinks}>{storeLinks}</Collapse>
                    </>
                )}
            </Collapse>
        </div>
    );
}
